import React, { useState, useMemo } from 'react';
import Icon from 'components/AppIcon';
import EmptyState from 'components/EmptyState';

export default function BuyerPerformancePanel({ sales, loading, onFilterByBuyer }) {
  const [sortField, setSortField] = useState('totalVolume');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');

  const buyerData = useMemo(() => {
    const groups = {};
    (sales || [])?.forEach(s => {
      const buyer = s?.buyer_name || 'Sin nombre';
      if (!groups?.[buyer]) groups[buyer] = { buyer, lots: 0, totalVolume: 0, totalValue: 0, margins: [], prices: [] };
      groups[buyer].lots += 1;
      groups[buyer].totalVolume += parseFloat(s?.weight_t) || 0;
      groups[buyer].totalValue += parseFloat(s?.total_sale_value) || 0;
      groups?.[buyer]?.prices?.push(parseFloat(s?.sale_price_per_ton) || 0);
      const entryPrice = parseFloat(s?.purchase_value) / Math.max(parseFloat(s?.weight_t), 0.001);
      const marginPct = entryPrice > 0 ? ((parseFloat(s?.sale_price_per_ton) - entryPrice) / entryPrice) * 100 : null;
      if (marginPct !== null) groups?.[buyer]?.margins?.push(marginPct);
    });
    return Object.values(groups)
      ?.map(g => ({
        buyer: g?.buyer,
        lots: g?.lots,
        totalVolume: g?.totalVolume,
        totalValue: g?.totalValue,
        avgPrice: g?.prices?.length > 0 ? g?.prices?.reduce((s, v) => s + v, 0) / g?.prices?.length : 0,
        avgMarginPct: g?.margins?.length > 0 ? g?.margins?.reduce((s, v) => s + v, 0) / g?.margins?.length : null,
      }));
  }, [sales]);

  const filtered = useMemo(() => {
    let list = buyerData;
    if (search?.trim()) {
      const q = search?.toLowerCase();
      list = list?.filter(b => b?.buyer?.toLowerCase()?.includes(q));
    }
    return list?.sort((a, b) => {
      const av = a?.[sortField];
      const bv = b?.[sortField];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      const cmp = typeof av === 'string' ? av?.localeCompare(bv) : (av - bv);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [buyerData, search, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const COLS = [
    { key: 'buyer', label: 'Comprador' },
    { key: 'lots', label: 'Lotes' },
    { key: 'totalVolume', label: 'Volumen (t)' },
    { key: 'avgPrice', label: 'Precio Medio €/t' },
    { key: 'avgMarginPct', label: 'Margen Medio %' },
    { key: 'totalValue', label: 'Valor Total €' },
  ];

  return (
    <div className="bg-card border border-border rounded-xl" style={{ boxShadow: 'var(--shadow-sm)' }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="Users" size={15} color="var(--color-accent)" strokeWidth={2} />
          <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Rendimiento por Comprador</h2>
          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{filtered?.length}</span>
        </div>
        <div className="relative">
          <Icon name="Search" size={13} color="var(--color-muted-foreground)" className="absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e?.target?.value)}
            placeholder="Buscar comprador..."
            className="pl-8 pr-3 py-1.5 text-xs bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-accent w-44"
            style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
          <thead>
            <tr className="border-b border-border">
              {COLS?.map(col => (
                <th
                  key={col?.key}
                  onClick={() => handleSort(col?.key)}
                  className="px-4 py-2.5 text-left text-muted-foreground font-medium cursor-pointer hover:text-foreground select-none whitespace-nowrap"
                >
                  <div className="flex items-center gap-1">
                    {col?.label}
                    {sortField === col?.key
                      ? <Icon name={sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={11} color="var(--color-accent)" />
                      : <Icon name="ChevronsUpDown" size={11} color="var(--color-muted-foreground)" />}
                  </div>
                </th>
              ))}
              {onFilterByBuyer && (
                <th className="px-4 py-2.5 text-center text-muted-foreground font-medium">Ventas</th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 3 })?.map((_, i) => (
                <tr key={i} className="border-b border-border/50">
                  {[...COLS, ...(onFilterByBuyer ? [{}] : [])]?.map((col, j) => (
                    <td key={j} className="px-4 py-2.5"><div className="h-3.5 bg-muted rounded animate-pulse" /></td>
                  ))}
                </tr>
              ))
            ) : filtered?.length === 0 ? (
              <EmptyState
                icon="Users"
                title="Sin compradores registrados"
                description="Sin compradores registrados todavía."
                colSpan={COLS?.length + (onFilterByBuyer ? 1 : 0)}
              />
            ) : (
              filtered?.map((row, idx) => (
                <tr key={idx} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-2.5 font-medium text-foreground">{row?.buyer}</td>
                  <td className="px-4 py-2.5 font-mono text-right">{row?.lots}</td>
                  <td className="px-4 py-2.5 font-mono text-right">{row?.totalVolume?.toLocaleString('es-ES', { maximumFractionDigits: 1 })}</td>
                  <td className="px-4 py-2.5 font-mono text-right">{row?.avgPrice?.toLocaleString('es-ES', { maximumFractionDigits: 0 })}</td>
                  <td className="px-4 py-2.5 text-right">
                    {row?.avgMarginPct !== null ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-mono font-semibold ${
                        row?.avgMarginPct >= 12 ? 'bg-emerald-100 text-emerald-700'
                        : row?.avgMarginPct >= 8 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {row?.avgMarginPct?.toFixed(1)}%
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-right">{row?.totalValue?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €</td>
                  {onFilterByBuyer && (
                    <td className="px-4 py-2.5 text-center">
                      <button
                        onClick={() => onFilterByBuyer(row?.buyer)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md border border-border hover:bg-muted transition-colors text-foreground"
                        style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
                        title={`Ver ventas de ${row?.buyer}`}
                      >
                        <Icon name="ExternalLink" size={10} color="var(--color-accent)" />
                        Ver ventas
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
